<script setup lang="ts">
import {
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Textarea,
    ZhButton,
    ZhSectionHeading,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { api, refusalCode, requestFailure, type TeamShareReview } from '@/api'
import RoleListingTasks from '@/components/RoleListingTasks.vue'
import { initiallyTicked, refusalSentence, saveListing } from '@/lib/catalogListing'
import { BUSINESS_FUNCTION_OPTIONS } from '@/lib/catalogText'

/**
 * Share a team to the catalog from its own page (#1844), or bring its entry up to date. The dialog reads what the
 * share would publish, lets the person set the words the catalog shows and where it files the team, and choose what
 * goes: each of the team's roles is ticked in or left out, and under each role going, each of its tasks is ticked in
 * or kept private, as on the role's own page (`RoleListingTasks`). Roles left out stay left out the next time the team
 * is shared. Nothing of the members travels, and the dialog never shows a member.
 *
 * Each role's ticks are saved first, one task at a time through the task's own version save (`saveListing`); a task
 * the catalog refuses (tenant passages, a missing handler) stops the share before the team is published, with the
 * server's own sentence beside the task, so the person can fix it and share again. Then the team is shared with the
 * roles that go, which publishes any of them the catalog does not hold yet.
 */
const props = defineProps<{ teamId: string; open: boolean }>()
const emit = defineEmits<{
    'update:open': [open: boolean]
    /** The team was shared: the review as it stands after. */
    shared: [review: TeamShareReview]
}>()

const NO_FUNCTION = 'none'

type ShareRole = TeamShareReview['roles'][number]

const review = ref<TeamShareReview | null>(null)
const loadFailed = ref(false)
const name = ref('')
const description = ref('')
const kindOfWork = ref('')
const businessFunction = ref(NO_FUNCTION)
/** The roles that go, by slug. */
const includedRoles = ref<Set<string>>(new Set())
/** Per role slug, the tasks ticked to be listed on it, by task id. */
const ticked = ref<Record<string, Set<string>>>({})
const busy = ref(false)
const status = ref('')
const error = ref('')
/** Per task (or stray entry), why its save was refused, shown beside it until the person fixes it and shares again. */
const taskErrors = ref<Record<string, string>>({})
/** The plan refuses publishing: the share says so and links Billing instead of trying. */
const planRefused = computed(() => review.value !== null && !review.value.canPublish)
const going = computed(() => (review.value?.roles ?? []).filter(role => includedRoles.value.has(role.slug)))
const heldElsewhere = computed(() => going.value.filter(role => role.heldElsewhere))
const ready = computed(
    () =>
        review.value !== null &&
        !busy.value &&
        !planRefused.value &&
        heldElsewhere.value.length === 0 &&
        going.value.length > 0 &&
        name.value.trim() !== '',
)

async function load(): Promise<void> {
    review.value = null
    loadFailed.value = false
    error.value = ''
    status.value = ''
    taskErrors.value = {}
    try {
        const loaded = await api.teamShareReview(props.teamId)
        review.value = loaded
        name.value = loaded.shared?.name ?? loaded.team.name
        description.value = loaded.team.goal
        includedRoles.value = new Set(loaded.roles.filter(role => role.included ?? true).map(role => role.slug))
        ticked.value = Object.fromEntries(loaded.roles.map(role => [role.slug, initiallyTicked(role.tasks ?? [])]))
    } catch {
        loadFailed.value = true
    }
}

watch(
    () => props.open,
    open => {
        if (open) void load()
    },
    { immediate: true },
)

function close(): void {
    emit('update:open', false)
}

function setRole(slug: string, on: boolean): void {
    const next = new Set(includedRoles.value)
    if (on) next.add(slug)
    else next.delete(slug)
    includedRoles.value = next
}

function setTask(slug: string, taskId: string, on: boolean): void {
    const next = new Set(ticked.value[slug] ?? [])
    if (on) next.add(taskId)
    else next.delete(taskId)
    ticked.value = { ...ticked.value, [slug]: next }
}

function roleState(role: ShareRole): string {
    if (role.heldElsewhere) return 'Held by another publisher'
    if (!includedRoles.value.has(role.slug)) return 'Left out'
    return role.public ? 'In the catalog' : 'Will be made public'
}

async function share(): Promise<void> {
    const loaded = review.value
    if (!loaded || !ready.value) return
    busy.value = true
    error.value = ''
    status.value = ''
    taskErrors.value = {}
    try {
        const refused: Record<string, string> = {}
        for (const role of going.value) {
            Object.assign(
                refused,
                await saveListing(
                    role.tasks ?? [],
                    ticked.value[role.slug] ?? new Set(),
                    role.strays ?? [],
                    sentence => {
                        status.value = sentence
                    },
                ),
            )
        }
        if (Object.keys(refused).length > 0) {
            taskErrors.value = refused
            status.value = ''
            error.value = 'A task could not be saved. Fix what it says, or untick it, and share again.'
            return
        }
        status.value = `Sharing ${name.value.trim()}…`
        await api.shareTeam(props.teamId, {
            roleSlugs: going.value.map(role => role.slug),
            name: name.value.trim(),
            description: description.value.trim(),
            kindOfWork: kindOfWork.value.trim() || null,
            businessFunction: businessFunction.value === NO_FUNCTION ? null : businessFunction.value,
        })
        const after = await api.teamShareReview(props.teamId)
        emit('shared', after)
        close()
    } catch (caught) {
        status.value = ''
        const code = refusalCode(caught)
        if (requestFailure(caught)?.status === 402 || code === 'catalog_publish_not_in_plan') {
            error.value = 'Your plan does not include catalog publishing.'
            if (review.value) review.value = { ...review.value, canPublish: false }
        } else if (code === 'catalog_role_slug_taken') {
            error.value =
                'The catalog already has a public role with one of these names, from another publisher. Rename that role and share again.'
        } else if (code === 'invalid_kind_of_work') {
            error.value = 'A kind of work is one lowercase word, such as support or marketing.'
        } else {
            error.value = refusalSentence(caught, 'Could not share the team.')
        }
    } finally {
        busy.value = false
    }
}
</script>

<template>
    <Dialog :open="open" @update:open="value => { if (!value) close() }">
        <DialogContent class="max-h-4/5 overflow-y-auto sm:max-w-2xl" data-testid="team-share-dialog">
            <DialogHeader>
                <DialogTitle>{{ review?.shared ? 'Update this team in the catalog' : 'Share this team' }}</DialogTitle>
                <DialogDescription>
                    Publishes the team to the catalog, where any company can clone it. The work travels: its roles, its
                    tasks, its KPIs and the cadence of its schedules. Your people, connected accounts, memory and run
                    history stay with you.
                </DialogDescription>
            </DialogHeader>
            <p v-if="loadFailed" class="text-destructive text-sm" role="alert">
                Could not read what sharing would publish. Close this and try again.
            </p>
            <p v-else-if="!review" class="text-muted-foreground text-sm">Reading the team…</p>
            <div v-else class="space-y-5">
                <p v-if="planRefused" class="text-sm" role="alert" data-testid="team-share-plan">
                    Your plan does not include catalog publishing.
                    <RouterLink class="underline" to="/billing#plans">See plans</RouterLink>
                </p>
                <div class="grid gap-3">
                    <Label class="grid gap-1 text-sm" for="team-share-name">
                        Name in the catalog
                        <Input id="team-share-name" v-model="name" maxlength="200" :disabled="busy" />
                    </Label>
                    <Label class="grid gap-1 text-sm" for="team-share-description">
                        One line
                        <Textarea
                            id="team-share-description"
                            v-model="description"
                            rows="2"
                            maxlength="280"
                            :disabled="busy"
                            placeholder="What the team does, in one line"
                        />
                    </Label>
                    <div class="grid gap-1 text-sm">
                        <Label for="team-share-kind">Kind of work</Label>
                        <Input
                            id="team-share-kind"
                            v-model="kindOfWork"
                            maxlength="64"
                            :disabled="busy"
                            placeholder="support, marketing, engineering…"
                        />
                        <p class="text-muted-foreground text-xs">
                            Setup offers teams by kind of work. One lowercase word; leave it blank to offer the team
                            nowhere in setup.
                        </p>
                        <div v-if="review.kindsOfWork.length" class="flex flex-wrap gap-2" role="group" aria-label="Kinds of work already in the catalog">
                            <ZhButton
                                v-for="kind in review.kindsOfWork"
                                :key="kind"
                                variant="ghost"
                                :aria-pressed="kindOfWork === kind ? 'true' : 'false'"
                                :disabled="busy"
                                @click="kindOfWork = kind"
                            >
                                {{ kind }}
                            </ZhButton>
                        </div>
                    </div>
                    <div class="grid gap-1 text-sm">
                        <span id="team-share-function">Business function</span>
                        <Select v-model="businessFunction" :disabled="busy">
                            <SelectTrigger aria-labelledby="team-share-function"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem :value="NO_FUNCTION">None</SelectItem>
                                <SelectItem v-for="option in BUSINESS_FUNCTION_OPTIONS" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <section class="space-y-3" aria-labelledby="team-share-roles">
                    <ZhSectionHeading id="team-share-roles" as="h3">Roles and their tasks</ZhSectionHeading>
                    <p v-if="!review.roles.length" class="text-muted-foreground text-sm" data-testid="team-share-no-roles">
                        This team has no roles yet, so there is nothing to clone. Give its members roles first.
                    </p>
                    <template v-else>
                        <p class="text-muted-foreground text-xs">
                            Tick the roles that go with the team, and under each the tasks it lists. A role left out
                            stays left out next time; an unticked task stays private.
                        </p>
                        <ul class="space-y-3">
                            <li
                                v-for="role in review.roles"
                                :key="role.id"
                                class="space-y-2 text-sm"
                                :data-testid="`team-share-role-${role.slug}`"
                            >
                                <div class="flex flex-wrap items-center gap-2">
                                    <Checkbox
                                        :id="`team-share-role-${role.id}`"
                                        :model-value="includedRoles.has(role.slug)"
                                        :disabled="busy"
                                        :data-testid="`team-share-include-${role.slug}`"
                                        @update:model-value="setRole(role.slug, $event === true)"
                                    />
                                    <Label :for="`team-share-role-${role.id}`" class="font-medium">{{ role.name }}</Label>
                                    <ZhSystemLabel as="span" :tone="role.heldElsewhere ? 'system' : 'muted'">
                                        {{ roleState(role) }}
                                    </ZhSystemLabel>
                                    <ZhSystemLabel v-if="(role.changes ?? 0) > 0" as="span" tone="system">
                                        {{ role.changes }} change{{ role.changes === 1 ? '' : 's' }}
                                    </ZhSystemLabel>
                                </div>
                                <RoleListingTasks
                                    v-if="includedRoles.has(role.slug)"
                                    class="border-l pl-4"
                                    :id-prefix="`team-share-${role.slug}`"
                                    :tasks="role.tasks ?? []"
                                    :strays="role.strays ?? []"
                                    :ticked="ticked[role.slug] ?? new Set()"
                                    :role-public="role.public && !role.heldElsewhere"
                                    :disabled="busy"
                                    :errors="taskErrors"
                                    @toggle="(taskId, on) => setTask(role.slug, taskId, on)"
                                />
                            </li>
                        </ul>
                    </template>
                    <ul v-if="review.removedRoles?.length" class="space-y-1">
                        <li
                            v-for="role in review.removedRoles"
                            :key="role.slug"
                            class="flex flex-wrap items-center gap-2 text-sm"
                            :data-testid="`team-share-removed-${role.slug}`"
                        >
                            <span class="text-muted-foreground">{{ role.name }}</span>
                            <ZhSystemLabel as="span" tone="system">No longer on the team: comes off the entry</ZhSystemLabel>
                        </li>
                    </ul>
                    <p v-if="heldElsewhere.length" class="text-destructive text-sm" role="alert" data-testid="team-share-held">
                        A public role with this name already comes from another publisher. Rename it, or leave it out,
                        to share the team.
                    </p>
                </section>

                <p class="text-muted-foreground text-sm" data-testid="team-share-travels">
                    {{ review.kpis }} KPI{{ review.kpis === 1 ? '' : 's' }} and {{ review.schedules }} schedule
                    cadence{{ review.schedules === 1 ? '' : 's' }} travel with the team. Members, credentials and memory
                    never do.
                </p>
                <p v-if="status" class="text-sm" role="status">{{ status }}</p>
                <p v-if="error" class="text-destructive text-sm" role="alert" data-testid="team-share-error">{{ error }}</p>
            </div>
            <DialogFooter>
                <Button type="button" variant="outline" :disabled="busy" @click="close">Cancel</Button>
                <Button type="button" :disabled="!ready" data-testid="team-share-submit" @click="share">
                    {{ review?.shared ? 'Update catalog' : 'Share' }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
